// src/encounter/lib/chart-card-model.ts
//
// The pure rules the chart-piece value card draws by (`phrase-chart-card.tsx`): how a lab's own flag
// reads for DS-020, and the words under a lab value. Nothing here computes a flag — it reads the
// flag CODE the laboratory printed (exact H / HH up, L / LL down; any other non-normal code is
// "mild" with no direction, because a flag that cannot be read is never read as normal).

import type { ChartResult } from './note-pieces';
import { dateText } from './to-chart-labs';
import { labDateText } from '@/patients/lib/lab-results';

export type FlagSeverity = 'none' | 'mild' | 'critical';

/** The lab's flag read for DS-020: how loud, and which way. Exact codes only; never inferred. */
export function readCarriedFlag(flag: string | undefined): { severity: FlagSeverity; dir: 'above' | 'below' | null } {
  const raw = (flag ?? '').trim();
  if (isQuietNormalFlag(raw)) return { severity: 'none', dir: null };
  const severity: FlagSeverity = raw === 'HH' || raw === 'LL' || raw === 'AA' ? 'critical' : 'mild';
  const dir = raw === 'H' || raw === 'HH' ? 'above' : raw === 'L' || raw === 'LL' ? 'below' : null;
  return { severity, dir };
}

/** ↑ / ↓, doubled when critical; nothing when the flag carries no direction. */
export function carriedFlagGlyph(flag: string | undefined): string {
  const { severity, dir } = readCarriedFlag(flag);
  if (severity === 'none' || !dir) return '';
  const g = dir === 'above' ? '↑' : '↓';
  return severity === 'critical' ? `${g}${g}` : g;
}

/** "Sep 12, 2026", else "Reported Sep 14, 2026", else "Date not recorded". */
export function labDate(r: Pick<ChartResult, 'collectedAt' | 'collectedAtPrecision' | 'reportedAt'>): string {
  // A Day or Month date keeps the calendar date the lab printed; a Month date reads "Sep 2026".
  if (r.collectedAt) return labDateText(r.collectedAt, r.collectedAtPrecision) ?? dateText(r.collectedAt);
  return r.reportedAt ? `Reported ${dateText(r.reportedAt)}` : 'Date not recorded';
}

/**
 * QUIET NORMAL (design desk, #614): the lab said normal — an explicit `N`, the word "normal" in any case,
 * or no flag at all. The tile's face then shows no flag; any other string the lab printed is shown
 * verbatim. The NOTE TEXT is unchanged ("lab flag N"): this only quiets the tile.
 */
export function isQuietNormalFlag(flag: string | null | undefined): boolean {
  const raw = (flag ?? '').trim();
  return raw === '' || raw === 'N' || raw.toLowerCase() === 'normal';
}

/** The tile's meta parts — date, laboratory, and the lab's flag unless it is quiet normal. Each part is
 *  drawn unbroken; only the separators between parts may wrap. */
export function labMetaParts(r: ChartResult): string[] {
  const parts = [labDate(r), r.lab?.trim() || 'lab not recorded'];
  if (!isQuietNormalFlag(r.flag)) parts.push(`lab flag ${(r.flag ?? '').trim()}`);
  return parts;
}

/** The meta line as one string (the tile's hover text). */
export function labMeta(r: ChartResult): string {
  return labMetaParts(r).join(' · ');
}
